/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

import React from 'react';
import pt from 'prop-types';
import cx from 'classnames';
import { cloneDeep, get } from 'lodash';
import { Droppable, Draggable, DragDropContext } from 'react-beautiful-dnd';
import { EasyblocksBackendContext } from 'components/storefront/editor/backendContext';
import Loading from 'components/loading';
import Accordion from 'components/accordion';
import Icon from 'components/icon';
import Tooltip from 'components/tooltip';
import { StorefrontEditorSidebarIcon } from 'components/storefront/editor/icon';
import {
  SWELL_GLOBAL,
  getDocumentPage,
  addSwellPagePrefix,
  parseItemPath,
  getCanvasElement,
} from 'components/storefront/editor/utils/easyblocks';
import {
  MESSAGE_EVENTS,
  postEasyblocksFocus,
  postEasyblocksItemMoved,
  postEasyblocksToggleItem,
  postStorefrontOverlayAddPageItem,
  postStorefrontOverlayPageItemEllipsis,
  postStorefrontItemHover,
  postStorefrontItemUnhover,
} from 'components/storefront/editor/utils/post-message';
import {
  getSectionsToAdd,
  getBlocksToAdd,
} from 'components/storefront/editor/utils/overlay';

import './sidebar.scss';

const LOADING_SIZE = 50;
const SCROLL_TIMEOUT = 1000;

export default class StorefrontEditorPageEdit extends React.PureComponent {
  static propTypes = {
    page: pt.object,
    focusedField: pt.string,
    editorConfig: pt.object,
    sectionGroups: pt.array,
    easyblocksConfig: pt.object,
    loading: pt.bool,
  };

  /** @type {AbortController | null} */
  messageEventController = null;

  /** @type {AbortController | null} */
  scrollEventController = null;

  /** @type {HTMLDivElement | null} */
  sectionGroupsContainer = null;

  /** @type {NodeJS.Timeout | null} */
  scrollTimeout = null;

  constructor(props) {
    super(props);

    this.state = {
      document: null,
      allSections: this.getSections(props),
      openSections: {},
      hoveredItem: null,
      activeItemModal: null,
      draggableSectionId: null,
      draggableBlockId: null,
    };
  }

  componentDidMount() {
    this.setState({ document: this.getDocument() });
    this.addEventListeners();
  }

  componentDidUpdate(prevProps) {
    const { focusedField } = this.props;

    if (focusedField !== prevProps.focusedField) {
      this.onFocus();
    }
  }

  componentWillUnmount() {
    this.removeEventListeners();
  }

  addEventListeners() {
    this.addMessageEventListener();
  }

  removeEventListeners() {
    this.removeMessageEventListener();
    this.removeScrollEventListener();
  }

  addMessageEventListener() {
    this.messageEventController = new AbortController();

    window.addEventListener('message', this.eventListener, {
      signal: this.messageEventController.signal,
    });
  }

  removeMessageEventListener() {
    if (this.messageEventController) {
      this.messageEventController.abort();
    }

    window.removeEventListener('message', this.eventListener);
  }

  setScrollEventListener = (container) => {
    if (container) {
      this.addScrollEventListener(container);
    } else {
      this.removeScrollEventListener();
    }
  };

  addScrollEventListener(container) {
    if (!container) {
      return;
    }

    this.sectionGroupsContainer = container;
    this.scrollEventController = new AbortController();

    this.sectionGroupsContainer.addEventListener(
      'scroll',
      this.scrollEventListener,
      {
        signal: this.scrollEventController.signal,
      },
    );
  }

  removeScrollEventListener() {
    if (!this.sectionGroupsContainer) {
      return;
    }

    if (this.scrollEventController) {
      this.scrollEventController.abort();
    }

    this.sectionGroupsContainer.removeEventListener(
      'scroll',
      this.scrollEventListener,
    );
    this.sectionGroupsContainer = null;

    clearTimeout(this.scrollTimeout);
  }

  /** @param {MessageEvent} event */
  eventListener = (event) => {
    const { type, payload } = event.data;

    switch (type) {
      case MESSAGE_EVENTS.EASYBLOCKS.BACKEND.DOCUMENT.UPDATED:
        return this.onDocumentUpdated();

      case MESSAGE_EVENTS.STOREFRONT_EDITOR.OVERLAY.CLOSED:
        return this.setState({
          activeItemModal: null,
        });

      case MESSAGE_EVENTS.STOREFRONT_EDITOR.ITEM.HOVER:
        return this.onHover(payload.path);

      case MESSAGE_EVENTS.STOREFRONT_EDITOR.ITEM.UNHOVER:
        return this.onUnhover(payload.path);

      default:
        return;
    }
  };

  scrollEventListener = () => {
    if (!this.sectionGroupsContainer) return;

    this.sectionGroupsContainer.classList.add('scroll-active');

    clearTimeout(this.scrollTimeout);

    this.scrollTimeout = setTimeout(() => {
      this.sectionGroupsContainer.classList.remove('scroll-active');
    }, SCROLL_TIMEOUT);
  };

  getDocument() {
    const { page } = this.props;
    const { easyblocksState } = this.context;

    return getDocumentPage(page.id, easyblocksState);
  }

  onDocumentUpdated() {
    this.setState({ document: this.getDocument() });
  }

  getElement(elementId) {
    const { document } = this.state;

    return get(document, elementId);
  }

  getElementId(elementPath) {
    const element = this.getElement(elementPath);

    return element?._id;
  }

  isItemDisabled(elementId) {
    const element = this.getElement(elementId);

    return Boolean(element?.disabled);
  }

  isActiveSection(sectionId) {
    const { focusedField } = this.props;
    const { draggableSectionId } = this.state;

    return draggableSectionId
      ? draggableSectionId === sectionId
      : Boolean(focusedField?.startsWith(sectionId));
  }

  isSectionOpen(sectionId) {
    const { openSections } = this.state;
    const elementId = this.getElementId(sectionId);

    if (!elementId) {
      return false;
    }

    return Boolean(openSections[elementId]);
  }

  onFocus() {
    const { focusedField } = this.props;
    const [sectionId] = parseItemPath(focusedField);

    if (!sectionId) {
      return;
    }

    const elementId = this.getElementId(sectionId);

    if (!elementId) {
      return;
    }

    this.setState((state) => ({
      openSections: {
        ...state.openSections,
        [elementId]: true,
      },
    }));
  }

  onClickChevron = (event, sectionId) => {
    event.stopPropagation();
    const { openSections } = this.state;
    const elementId = this.getElementId(sectionId);

    if (!elementId) {
      return;
    }

    const isOpen = openSections[elementId];

    if (!isOpen) {
      postEasyblocksFocus(sectionId);
    }

    this.setState((state) => ({
      openSections: {
        ...state.openSections,
        [elementId]: !isOpen,
      },
    }));
  };

  onClickSection = (sectionId) => {
    postEasyblocksFocus(sectionId);

    const elementId = this.getElementId(sectionId);

    if (!elementId) {
      return;
    }

    this.setState((state) => ({
      openSections: {
        ...state.openSections,
        [elementId]: true,
      },
    }));
  };

  onClickBlock = (blockId) => {
    postEasyblocksFocus(blockId);
  };

  onClickAddItem = (event, path) => {
    const domRect = event.target.getBoundingClientRect();

    postStorefrontOverlayAddPageItem(domRect, path);
  };

  onClickHideItem = (event) => {
    event.stopPropagation();
    const { disabling } = event.currentTarget.dataset;
    const path = addSwellPagePrefix(event.currentTarget.dataset.path);

    postEasyblocksToggleItem(path);

    if (disabling) {
      postEasyblocksFocus(SWELL_GLOBAL);
    }
  };

  onClickItemEllipsis = (event) => {
    event.stopPropagation();
    const { path } = event.currentTarget.dataset;
    const domRect = event.target.getBoundingClientRect();

    postStorefrontOverlayPageItemEllipsis(domRect, path);
    postEasyblocksFocus(addSwellPagePrefix(path));

    this.setState({ activeItemModal: path });
  };

  onDragStart = (result) => {
    const { draggableId } = result;

    if (!draggableId) {
      return;
    }

    const [sectionId, blockId] = parseItemPath(draggableId);

    if (blockId) {
      this.setState({ draggableBlockId: draggableId });
    } else if (sectionId) {
      this.setState({ draggableSectionId: draggableId });
    }
  };

  onDragEnd = (result) => {
    const { source, destination } = result;

    this.setState({ draggableSectionId: null, draggableBlockId: null });

    // dropped outside the list
    if (!destination) {
      return;
    }

    const { index: sourceIndex, droppableId: sourcePath } = source;
    const { index: destinationIndex, droppableId: destinationPath } =
      destination;
    const fromPath = `${sourcePath}.${sourceIndex}`;
    const toPath = `${destinationPath}.${destinationIndex}`;

    if (fromPath === toPath) {
      return;
    }

    const easyblocksFromPath = addSwellPagePrefix(fromPath);
    const easyblocksToPath = addSwellPagePrefix(toPath);

    postEasyblocksItemMoved(easyblocksFromPath, easyblocksToPath);
    postEasyblocksFocus(easyblocksToPath);

    this.moveDocumentItem(fromPath, toPath);
  };

  moveDocumentItem(fromPath, toPath) {
    const { document } = this.state;
    const newDocument = cloneDeep(document);
    const [fromSectionGroup, fromSectionIndex, fromBlockGroup, fromBlockIndex] =
      fromPath.split('.');
    const [, toSectionIndex, , toBlockIndex] = toPath.split('.');
    const item = get(document, fromPath);

    // move item
    if (fromBlockGroup) {
      // move block
      const blocks =
        newDocument[fromSectionGroup][fromSectionIndex][fromBlockGroup];

      blocks.splice(fromBlockIndex, 1);
      blocks.splice(toBlockIndex, 0, item);
    } else {
      // move section
      const sections = newDocument[fromSectionGroup];

      sections.splice(fromSectionIndex, 1);
      sections.splice(toSectionIndex, 0, item);
    }

    this.setState({
      document: newDocument,
    });
  }

  onHover(path) {
    this.setState({ hoveredItem: path });
  }

  onUnhover(path) {
    const { hoveredItem } = this.state;

    if (path === hoveredItem) {
      return this.setState({ hoveredItem: null });
    }
  }

  hoverItem = (event) => {
    const { id } = event.currentTarget.dataset;
    const canvasElement = getCanvasElement();

    if (canvasElement) {
      postStorefrontItemHover(id, canvasElement.contentWindow);
    }
  };

  unhoverItem = (event) => {
    const { id } = event.currentTarget.dataset;
    const canvasElement = getCanvasElement();

    if (canvasElement) {
      postStorefrontItemUnhover(id, canvasElement.contentWindow);
    }
  };

  getSections(props = this.props) {
    const { allSections } = props.editorConfig;

    return allSections.reduce((acc, section) => {
      acc[section.id] = section;

      return acc;
    }, {});
  }

  getBlockGroupId(sectionId) {
    return `${sectionId}.Blocks`;
  }

  getBlockId(sectionId, index) {
    const blockGroupId = this.getBlockGroupId(sectionId);

    return `${blockGroupId}.${index}`;
  }

  renderElementActions(path, showItemEllipsis = true) {
    const { loading } = this.props;
    const isDisabled = this.isItemDisabled(path);

    return (
      <div className={'storefront-editor--page-edit--element-actions'}>
        <button
          type="button"
          data-path={path}
          data-disabling={!isDisabled}
          onClick={this.onClickHideItem}
          disabled={loading}
        >
          <Icon svgType={isDisabled ? 'eye-off' : 'eye'} />
        </button>
        {showItemEllipsis && (
          <button
            type="button"
            data-path={path}
            onClick={this.onClickItemEllipsis}
            disabled={loading}
          >
            <Icon fa="ellipsis-h" />
          </button>
        )}
      </div>
    );
  }

  renderSectionBlock(block, section, sectionId, index) {
    const { loading, focusedField } = this.props;
    const { allSections, activeItemModal, hoveredItem, draggableBlockId } =
      this.state;
    const { _component: sectionComponent } = section;
    const { _component: blockComponent } = block;
    const sectionConfig = allSections[sectionComponent];

    const blockConfig = sectionConfig.blocks.find(
      (blockConfig) =>
        `Block__${sectionComponent}__${blockConfig.type}` === blockComponent,
    );

    if (!blockConfig) {
      return null;
    }

    const blockId = this.getBlockId(sectionId, index);
    const isActive = draggableBlockId
      ? draggableBlockId === blockId
      : focusedField === blockId;
    const isModalActive = activeItemModal === blockId;
    const disabled = this.isItemDisabled(blockId);

    return (
      <Draggable
        key={blockId}
        draggableId={blockId}
        index={index}
        isDragDisabled={loading}
      >
        {(provided) => (
          <div
            className="storefront-editor--page-edit--block-container"
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
          >
            <div
              className={cx('storefront-editor--page-edit--block', {
                active: isActive,
                'active-modal': isModalActive,
                hovered: hoveredItem === blockId,
              })}
              data-id={blockId}
              onMouseEnter={this.hoverItem}
              onMouseLeave={this.unhoverItem}
              onClick={() => this.onClickBlock(blockId)}
            >
              <div
                className={cx('storefront-editor--page-edit--block-label', {
                  disabled,
                })}
              >
                <StorefrontEditorSidebarIcon
                  type={blockConfig.label || blockConfig.type}
                />
                {blockConfig.label}
              </div>
              {this.renderElementActions(blockId)}
            </div>
            {provided.placeholder}
          </div>
        )}
      </Draggable>
    );
  }

  renderAddBlockButton(
    blockGroupId,
    { blocks: blocksToAdd, isLimitReached, limit },
  ) {
    const { loading } = this.props;
    const hasBlocksToAdd = blocksToAdd.length > 0;
    const shouldRenderButton = hasBlocksToAdd || isLimitReached;

    if (!shouldRenderButton) {
      return null;
    }

    const button = (
      <button
        type="button"
        className="storefront-editor--button-add"
        disabled={loading || isLimitReached}
        onClick={(event) => this.onClickAddItem(event, blockGroupId)}
      >
        <StorefrontEditorSidebarIcon type="plus-circle" />
        New block
      </button>
    );

    if (!isLimitReached) {
      return button;
    }

    return (
      <Tooltip
        message={`This section allows up to ${limit} blocks`}
        side="top"
        align="start"
        arrow={false}
      >
        {button}
      </Tooltip>
    );
  }

  renderSectionBlocks(section, sectionId, blocksToAdd) {
    const { draggableBlockId } = this.state;
    const { Blocks: blocks } = section;
    const blockGroupId = this.getBlockGroupId(sectionId);

    const renderedBlocks = (blocks || [])
      .map((block, index) =>
        this.renderSectionBlock(block, section, sectionId, index),
      )
      .filter(Boolean);

    const hasBlocks = renderedBlocks.length > 0;

    const droppable = Boolean(draggableBlockId?.startsWith(blockGroupId));

    return (
      <div
        className={cx('storefront-editor--page-edit--blocks-container', {
          droppable,
        })}
      >
        {hasBlocks && (
          <Droppable droppableId={blockGroupId} type="droppableBlock">
            {(provided) => (
              <div
                className="storefront-editor--page-edit--blocks"
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {renderedBlocks}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        )}
        {this.renderAddBlockButton(blockGroupId, blocksToAdd)}
      </div>
    );
  }

  renderSectionLabel(sectionConfig) {
    return (
      <div className="storefront-editor--page-edit--section-label">
        <StorefrontEditorSidebarIcon type={sectionConfig.label} />
        {sectionConfig.label || sectionConfig.name || sectionConfig.id}
      </div>
    );
  }

  renderSection(sectionGroupId, section, index) {
    if (!section) {
      return null;
    }

    const { editorConfig, easyblocksConfig, loading } = this.props;
    const { document, allSections, activeItemModal, hoveredItem } = this.state;

    const { Blocks: blocks, _component: sectionComponent } = section;
    const sectionId = `${sectionGroupId}.${index}`;
    const sectionConfig = allSections[sectionComponent];

    if (!sectionConfig) {
      return null;
    }

    const blockGroupId = this.getBlockGroupId(sectionId);
    const blocksToAdd = getBlocksToAdd(
      editorConfig,
      easyblocksConfig,
      document,
      blockGroupId,
    );
    const hasBlocks = blocks?.length > 0;
    const hasBlocksToAdd = blocksToAdd.blocks.length > 0;
    const shouldRenderBlocks =
      hasBlocks || hasBlocksToAdd || blocksToAdd.isLimitReached;
    const isOpen = this.isSectionOpen(sectionId) && shouldRenderBlocks;
    const isActive = this.isActiveSection(sectionId);
    const isModalActive = activeItemModal === sectionId;
    const isDisabled = this.isItemDisabled(sectionId);
    // do not show ellipsis and empty menu for sections without presets
    const showItemEllipsis = sectionConfig.presets?.length > 0;

    return (
      <Draggable
        key={sectionId}
        draggableId={sectionId}
        index={index}
        isDragDisabled={loading}
      >
        {(provided) => (
          <div
            className="storefront-editor--page-edit--section-container"
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
          >
            <div
              className={cx('storefront-editor--page-edit--section', {
                active: isActive,
                'active-modal': isModalActive,
                hovered: Boolean(hoveredItem?.includes(sectionId)),
              })}
            >
              <Accordion
                label={this.renderSectionLabel(sectionConfig)}
                headerRest={this.renderElementActions(
                  sectionId,
                  showItemEllipsis,
                )}
                headerProps={{
                  'data-id': sectionId,
                  onMouseEnter: this.hoverItem,
                  onMouseLeave: this.unhoverItem,
                  onClick: () => this.onClickSection(sectionId),
                }}
                onClickArrow={(event) => this.onClickChevron(event, sectionId)}
                isOpen={isOpen}
                labelButtonDisabled={isDisabled}
                arrow={shouldRenderBlocks}
              >
                {shouldRenderBlocks &&
                  this.renderSectionBlocks(section, sectionId, blocksToAdd)}
              </Accordion>
            </div>
            {provided.placeholder}
          </div>
        )}
      </Draggable>
    );
  }

  renderAddSectionButton(sectionGroupId) {
    const { editorConfig, easyblocksConfig, loading } = this.props;
    const sectionsToAdd = getSectionsToAdd(
      editorConfig,
      easyblocksConfig,
      sectionGroupId,
    );
    const canAddSection = sectionsToAdd.length > 0;

    if (!canAddSection) {
      return null;
    }

    return (
      <button
        type="button"
        className="storefront-editor--button-add"
        disabled={loading}
        onClick={(event) => this.onClickAddItem(event, sectionGroupId)}
      >
        <StorefrontEditorSidebarIcon type="plus-circle" />
        New section
      </button>
    );
  }

  renderSections(sectionGroupId, sections, title, group) {
    if (!group) {
      // TODO: Static section
      return (
        <div className="storefront-editor--page-edit--sections-container">
          <div className="storefront-editor--page-edit--sections">
            <p className="storefront-editor--page-edit--sections-title">
              {title}
            </p>
          </div>
        </div>
      );
    }

    const { draggableSectionId, draggableBlockId } = this.state;
    const dragging = Boolean(draggableSectionId || draggableBlockId);
    const droppable = Boolean(draggableSectionId?.startsWith(sectionGroupId));

    return (
      <DragDropContext
        key={sectionGroupId}
        onDragStart={this.onDragStart}
        onDragEnd={this.onDragEnd}
      >
        <div
          className={cx('storefront-editor--page-edit--sections-container', {
            dragging,
            droppable,
          })}
        >
          <p className="storefront-editor--page-edit--sections-title">
            {title}
          </p>
          <Droppable droppableId={sectionGroupId} type="droppableSection">
            {(provided) => (
              <div
                className="storefront-editor--page-edit--sections"
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {sections.map((section, index) =>
                  this.renderSection(sectionGroupId, section, index),
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          {this.renderAddSectionButton(sectionGroupId)}
        </div>
      </DragDropContext>
    );
  }

  renderPageSections() {
    const { document } = this.state;

    if (!document) {
      return null;
    }

    const { sectionGroups } = this.props;

    if (sectionGroups.length <= 0) {
      return null;
    }

    return (
      <div
        className="storefront-editor--page-edit--section-groups"
        ref={this.setScrollEventListener}
      >
        {sectionGroups.map((sectionGroup) =>
          this.renderSections(
            sectionGroup.prop,
            document[sectionGroup.prop] ?? [],
            sectionGroup.label,
            sectionGroup.group,
          ),
        )}
      </div>
    );
  }

  render() {
    const { easyblocksConfig } = this.props;
    const { document } = this.state;
    const loading = !document || !easyblocksConfig;

    return (
      <div className="storefront-editor--page-edit">
        {loading ? <Loading size={LOADING_SIZE} /> : this.renderPageSections()}
      </div>
    );
  }
}

StorefrontEditorPageEdit.contextType = EasyblocksBackendContext;
